import * as dte from "../DtEventTypes";
import { FacetsManager, MODEL_FACET_DEFS_IDX } from "./FacetsManager";
import { FacetTypes, FacetsScope } from "./Facets";
import { FacetsForScope } from "./FacetsForScope";
import { MultipleMepSystemsFacetColorId, MultipleRoomsFacetColorId } from "./ColorUtils";
import { sortedArrayHas } from "./sortedArray";

function convertColorMap(colorMap) {
  const colorMapV4 = {};
  for (let c in colorMap) {
    let hex = colorMap[c];
    if (hex) {
      // THREE's color parsing (which is used under the hood of applyTheme) only copes
      // with RGB hex colors, hence we strip the alpha channel here.
      // TODO: tweak THREE.Color.setStyle, or make it faster some other way
      if (hex.length === 9) {
        hex = hex.slice(0, 7);
      }
      const clr = new THREE.Color(hex);
      colorMapV4[c] = new THREE.Vector4(clr.r, clr.g, clr.b, 0.9);
    }
  }
  return colorMapV4;
}

function _applyThemeToModel(m, facetScope, facetIdx, setTheme) {
  // theme facets
  const facetDef = facetScope.facetDefs[facetIdx];

  const multipleFacetsColorId = facetDef.id === FacetTypes.mepSystems ? MultipleMepSystemsFacetColorId : MultipleRoomsFacetColorId;

  const filter = facetDef.filter;
  const seenMultiFacetElements = new Map();
  const idsInMultipleFacets = facetScope.idsInMultipleFacets[facetIdx]?.get(m);

  function applyThemeToNodes(mergedFacetNodes) {
    for (let node of mergedFacetNodes) {

      //Only process theming if the facet is not filtered out.
      if (filter.size === 0 || filter.has(node.id)) {
        const idSet = node.ids.get(m);
        if (idSet) {
          for (let id of idSet) {
            if (idsInMultipleFacets && sortedArrayHas(idsInMultipleFacets, id)) {
              //For elements that belong to potentially multiple facets, color them
              //by the facet color of the first matching facet, and then switch to the multi-filter color
              //in case they are in two or more visible facets
              const status = seenMultiFacetElements.get(id);
              if (!status) {
                seenMultiFacetElements.set(id, 1);
                setTheme(id, node.id);
              } else if (status === 1) {
                seenMultiFacetElements.set(id, 2);
                setTheme(id, multipleFacetsColorId);
              }
            } else {
              setTheme(id, node.id);
            }
          }
        }
      }

      if (node.children?.length) {
        applyThemeToNodes(node.children);
      }
    }
  }

  applyThemeToNodes(facetScope.mergedFacets[facetIdx]);
}

FacetsManager.prototype.getThemedFacetDef = function (scope = FacetsScope.Context) {
  return this.scoped(scope, (fs) => fs.themedFacet);
};

/** Applies or clear the theme applied to all elements in the scope. Does not support multi-scope invocation. */
FacetsManager.prototype.applyTheme = function (facetId, colorMap, scope = FacetsScope.Context) {
  const facetScope = this.facetsForScope[scope];

  // Clear any existing themes (since we only apply theme for elements in a filtered facet)
  if (facetScope.themedFacet && facetScope.themedFacet.id !== facetId) {
    facetScope.clearTheme();
    facetScope.themedFacet = undefined;
  }

  //Set new color theme on the requested facet
  const facetIdx = facetScope.getFacetDefIndex(facetId);
  if (facetIdx >= 0) {
    facetScope.applyTheme(facetIdx, colorMap);
    facetScope.themedFacet = facetScope.facetDefs[facetIdx];
  }

  this.eventTarget.dispatchEvent({ type: dte.DT_THEMING_CHANGED_EVENT, facetId, scope });
};

/**
 * Clears the theme applied to all elements in the scope. Does not support multi-scope invocation.
 * Note: This does not clear the theme for a specific facet,
 * use applyTheme with an empty colorMap if themedFacet is defined.
 */
FacetsManager.prototype.clearTheme = function (scope = FacetsScope.Context) {
  this.facetsForScope[scope].clearTheme();

  this.eventTarget.dispatchEvent({ type: dte.DT_THEMING_CHANGED_EVENT, scope });
};

/** Applies a new theming (DOES NOT CLEAR ON INVALID) */
FacetsForScope.prototype.applyTheme = function (facetIdx, colorMap) {
  switch (this.scope) {
    case FacetsScope.Context:
      const colorMapV4 = convertColorMap(colorMap);
      for (let m of this.fm.models) {
        if (facetIdx === MODEL_FACET_DEFS_IDX) {
          m.setThemingColor(m.getRootId(), colorMapV4[m.urn()], true);
        } else {
          // if needed we could check whether id is a logical or graphical element, and color accordingly.
          _applyThemeToModel(m, this, facetIdx, (dbId, facetNodeId) => m.setThemingColor(dbId, colorMapV4[facetNodeId]));
        }
      }
      this.fm.facetsEffects.makeRoomsVisible(true);
      this.fm.viewer.impl.invalidate(true);
      return;
    case FacetsScope.Tickets:
      for (let m of this.fm.models) {
        if (facetIdx === MODEL_FACET_DEFS_IDX) {
          m.setLogicalColor(m.getRootId(), colorMap[m.urn()], true);
        } else {
          _applyThemeToModel(m, this, facetIdx, (dbId, facetNodeId) => m.setLogicalColor(dbId, colorMap[facetNodeId]));
        }
      }
      return;
  }
};

/** Clears any applied theming */
FacetsForScope.prototype.clearTheme = function () {
  switch (this.scope) {
    case FacetsScope.Context:
      for (const model of this.fm.models) {
        model.setThemingColor(model.getRootId(), null /* colorMap */, true /* recursive */);
      }
      this.fm.facetsEffects.makeRoomsVisible(false);
      this.fm.viewer.impl.invalidate(true);
      return;
    case FacetsScope.Tickets:
      for (const model of this.fm.models) {
        model.clearLogicalColor();
      }
      return;
  }
};